import axiosInstance from "../config/axiosInstance";
import API from "../config/config";
import { toast } from "react-toastify";

class SubscriptionService {
  // Get subscription analytics
  static async getAnalytics() {
    try {
      const response = await axiosInstance.get(
        API.ADMIN.SUBSCRIPTION_ANALYTICS
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching subscription analytics:", error);
      // Return mock data for development
      return {
        analytics: {
          totalSubscriptions: 0,
          activeSubscriptions: 0,
          totalRevenue: 0,
          statusBreakdown: {
            ACTIVE: 0,
            EXPIRED: 0,
            CANCELLED: 0,
            PENDING: 0,
          },
        },
      };
    }
  }

  // Get revenue analytics
  static async getRevenueAnalytics(params = {}) {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await axiosInstance.get(
        `${API.ADMIN.SUBSCRIPTION_ANALYTICS}?${queryString}`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching revenue analytics:", error);
      // Return mock data for development
      return {
        revenueAnalytics: {
          revenueByPeriod: [],
          topDrivers: [],
          totalRevenue: 0,
          totalSubscriptions: 0,
        },
      };
    }
  }

  // Get all subscriptions with filtering
  static async getAllSubscriptions(params = {}) {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await axiosInstance.get(
        `${API.ADMIN.SUBSCRIPTION_LIST}?${queryString}`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching subscriptions:", error);
      // Return mock data for development
      return {
        subscriptions: [],
        pagination: {
          currentPage: 1,
          totalPages: 1,
          totalItems: 0,
          hasNext: false,
          hasPrev: false,
        },
      };
    }
  }

  // Get subscription by ID
  static async getSubscriptionById(subscriptionId) {
    try {
      const response = await axiosInstance.get(
        `${API.ADMIN.SUBSCRIPTION_DETAIL}/${subscriptionId}`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching subscription:", error);
      throw error;
    }
  }

  // Update subscription
  static async updateSubscription(subscriptionId, updateData) {
    try {
      const response = await axiosInstance.put(
        `${API.ADMIN.SUBSCRIPTION_UPDATE}/${subscriptionId}`,
        updateData,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      toast.success("Subscription updated successfully");
      return response.data;
    } catch (error) {
      console.error("Error updating subscription:", error);
      toast.error("Failed to update subscription");
      throw error;
    }
  }

  // Get unsubscribed drivers
  static async getUnsubscribedDrivers(params = {}) {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await axiosInstance.get(
        `${API.ADMIN.SUBSCRIPTION_UNSUBSCRIBED_DRIVERS}?${queryString}`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching unsubscribed drivers:", error);
      // Return mock data for development
      return {
        drivers: [],
        pagination: {
          currentPage: 1,
          totalPages: 1,
          totalItems: 0,
          hasNext: false,
          hasPrev: false,
        },
      };
    }
  }

  // Activate subscription
  static async activateSubscription(subscriptionId, duration = 30) {
    try {
      const response = await axiosInstance.post(
        `${API.ADMIN.SUBSCRIPTION_ACTIVATE}/${subscriptionId}/activate`,
        { duration },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      toast.success("Subscription activated successfully");
      return response.data;
    } catch (error) {
      console.error("Error activating subscription:", error);
      toast.error("Failed to activate subscription");
      throw error;
    }
  }

  // Expire subscription
  static async expireSubscription(subscriptionId, reason = "") {
    try {
      const response = await axiosInstance.post(
        `${API.ADMIN.SUBSCRIPTION_EXPIRE}/${subscriptionId}/expire`,
        { reason },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      toast.success("Subscription expired successfully");
      return response.data;
    } catch (error) {
      console.error("Error expiring subscription:", error);
      toast.error("Failed to expire subscription");
      throw error;
    }
  }

  // Bulk expire subscriptions
  static async bulkExpireSubscriptions(subscriptionIds, reason = "") {
    try {
      const response = await axiosInstance.post(
        API.ADMIN.SUBSCRIPTION_BULK_EXPIRE,
        { subscriptionIds, reason },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      toast.success(
        `Successfully expired ${subscriptionIds.length} subscriptions`
      );
      return response.data;
    } catch (error) {
      console.error("Error bulk expiring subscriptions:", error);
      toast.error("Failed to expire subscriptions");
      throw error;
    }
  }

  // Send renewal reminder
  static async sendRenewalReminder(subscriptionId, message) {
    try {
      const response = await axiosInstance.post(
        `${API.ADMIN.SUBSCRIPTION_REMIND}/${subscriptionId}/remind`,
        { message, driverId: subscriptionId },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      toast.success("Renewal reminder sent successfully");
      return response.data;
    } catch (error) {
      console.error("Error sending renewal reminder:", error);
      toast.error("Failed to send renewal reminder");
      throw error;
    }
  }

  // Auto-extend subscriptions for low-earning drivers
  static async autoExtendLowEarningDrivers(
    earningsThreshold = 1000,
    extensionDays = 30
  ) {
    try {
      const response = await axiosInstance.post(
        API.ADMIN.SUBSCRIPTION_AUTO_EXTEND,
        {
          earningsThreshold,
          extensionDays,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
        toast.success(
          `Auto extension completed: ${response.data.data.extendedCount} subscriptions extended`
        );
      }

      return response.data;
    } catch (error) {
      console.error("Error in auto subscription extension:", error);
      toast.error("Failed to process auto subscription extensions");
      throw error;
    }
  }

  // Export subscription data
  static async exportSubscriptions(params = {}) {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await axiosInstance.get(
        `${API.ADMIN.SUBSCRIPTION_EXPORT}?${queryString}`
      );

      // Create and download CSV file
      const csvContent = this.convertToCSV(response.data.data);
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        response.data.filename || "subscriptions_export.csv"
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success(`Exported ${response.data.data.length} subscriptions`);
      return response.data;
    } catch (error) {
      console.error("Error exporting subscriptions:", error);
      toast.error("Failed to export subscriptions");
      throw error;
    }
  }

  // Helper method to convert data to CSV
  static convertToCSV(data) {
    if (!data || data.length === 0) return "";

    const headers = Object.keys(data[0]);
    const csvHeaders = headers.join(",");
    const csvRows = data.map((row) =>
      headers
        .map((header) => {
          const value = row[header];
          // Escape commas and quotes in CSV
          return typeof value === "string" &&
            (value.includes(",") || value.includes('"'))
            ? `"${value.replace(/"/g, '""')}"`
            : value;
        })
        .join(",")
    );

    return [csvHeaders, ...csvRows].join("\n");
  }
}

export default SubscriptionService;
